import { Loading } from '@sandtech-hub-monorepo/ui-kit-site';

import { useGsaEnrollments } from '../../data/gsaEnrollments';
import { useLearnerPath } from '../../data/useLearnerPath';
import { ProgramPathChooser } from '../ProgramPathChooser';
import { GsaDashboard } from '../GsaDashboard';
import { DegreeHome } from './DegreeHome';

/**
 * The whole `/` route, and the only home either learner has: it renders per
 * path rather than redirecting, so one url means home for everyone and the
 * guard always has somewhere safe to send a learner it turns away.
 *
 * A degree learner gets the degree home. A GSA learner gets the dashboard once
 * they've ever had a GSA enrollment, and the path chooser until then. A
 * learner with both keeps the chooser, since nothing here can pick for them.
 *
 * Any GSA enrollment status counts towards the dashboard, not just ONGOING:
 * a learner who graduated, failed, or had their enrollment cancelled has
 * still demonstrated they're a GSA learner, and sending them back to the
 * chooser would ask them to pick a path they already took. Only `current`
 * (ONGOING) is handed to the dashboard itself - someone with no program
 * currently in progress sees it with an empty "My programs" section,
 * pointing at Learning, where the rest of their enrollment history shows.
 */
const Home = () => {
  const path = useLearnerPath();
  const {
    current,
    hasEverBeenGsaLearner,
    loading: enrollmentsLoading,
  } = useGsaEnrollments();

  // Without this the chooser renders first and is replaced a moment later,
  // which reads as the page changing its mind about who the learner is. The
  // path needs no such wait — the guard resolved it before this rendered.
  if (enrollmentsLoading) {
    return <Loading />;
  }

  if (path === 'degree') {
    return <DegreeHome />;
  }

  if (path === 'both') {
    return <ProgramPathChooser />;
  }

  // Handed down rather than queried again inside the dashboard: this gate
  // resolves first, so a second useQuery there would be created after the
  // first had settled and Apollo could not dedupe it into one request.
  return hasEverBeenGsaLearner ? (
    <GsaDashboard enrollments={current} />
  ) : (
    <ProgramPathChooser />
  );
};

export default Home;
